import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';
import { FORUM_DESCRIPTION_PLACEHOLDER } from '@/constants';
import {
  useForumTopicsRetrieve,
  useForumTopicsPostsList,
} from '@/api/forum/forum';
import ProjectList from '@/components/ProjectList';
import CookedText from '@/components/CookedText';
import { ClipLoader } from 'react-spinners';
import { formatDate } from '@/utils';

const TopicDetail: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const { data: topic, isLoading: isLoadingTopics } = useForumTopicsRetrieve(
    Number(topicId)
  );
  const { data: posts, isLoading: isLoadingPosts } = useForumTopicsPostsList(
    Number(topicId),
    {}
  );
  const navigate = useNavigate();

  if (isLoadingPosts || isLoadingTopics) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <ClipLoader
          size={50}
          color={'black'}
          loading={isLoadingPosts || isLoadingTopics}
        />
      </div>
    );
  }

  if (!topic) {
    return <div className='p-20'>Error loading data</div>;
  }

  const category =
    topic.category.topic_id === topic.id
      ? topic.category.parent_category
      : topic.category;

  const first_post = posts?.at(0);
  const firstPostContent = first_post!.cooked.includes(
    FORUM_DESCRIPTION_PLACEHOLDER
  )
    ? null
    : first_post!.cooked;

  const comments = posts?.slice(1);

  const showDescriptionSection =
    firstPostContent || (comments && comments.length > 0);
  const showSubTopicsSection = topic.sub_topics && topic.sub_topics.length > 0;

  return (
    <div className='container mx-auto p-6'>
      <div className='bg-white p-6 rounded-lg border-1 border-black'>
        <div className='flex items-center justify-between gap-12 pb-12'>
          <a
            href={`https://forum.opoint.com/t/${topic.id}`}
            target='_blank'
            rel='noopener noreferrer'
            className='no-underline text-black'
          >
            <h1 className='text-2xl font-bold flex-1 text-left font-verdana, text-black'>
              {topic.name}
            </h1>
          </a>
          <div className='flex items-center'>
            <p className='text-sm font-bold'>{category?.name}</p>
            <span
              className='w-3 h-3 rounded-full ml-2'
              style={{ backgroundColor: `#${category?.color}` }}
            ></span>
            <button
              onClick={() => navigate(-1)}
              className='flex items-left bg-white ml-4'
            >
              {topic.tags.some((tag) => tag.name === 'project') ? (
                <FaTimes size={15} />
              ) : (
                <FaArrowLeft size={15} />
              )}
            </button>
          </div>
        </div>

        <div className='flex'>
          <div className='w-3/4 text-left pr-12'>
            <div className='p-4 w-3xl text-sm font-bold text-black flex gap-4 mb-2'>
              <p>{formatDate(topic.created_at)}</p> |
              <p>{topic.user.username}</p>
            </div>
          </div>
          <div className='w-1/3 text-right space-y-2'>
            <p className='text-sm text-gray-500'>
              Last Activity: {formatDate(topic.last_posted_at!)}
            </p>
            <p className='text-sm text-gray-500 flex justify-end'>
              {topic.posts_count} posts
            </p>
            {/* <p className='text-sm text-gray-500 flex justify-end'>
              {topic.participant_count}{' '}
              participants
            </p> */}
          </div>
        </div>

        <div className='flex gap-6'>
          {showDescriptionSection && (
            <div
              className={`${showSubTopicsSection && 'w-3/4'} max-w-full text-left`}
            >
              {firstPostContent && (
                <div
                  className='space-y-2 overflow-y-auto border p-4 rounded-md b-1 border-black'
                  style={{ backgroundColor: 'var(--opoint-light-grey)' }}
                >
                  <CookedText cookedHtml={firstPostContent} />
                </div>
              )}

              {/* Comments Section */}
              {comments && comments.length > 0 && (
                <div className='text-left mx-auto'>
                  <p className='py-4 text-sm'>
                    Comments ({comments?.length || 0}):
                  </p>
                  <div
                    className='space-y-2 overflow-y-auto border p-4 rounded-md b-1 border-black'
                    style={{ backgroundColor: 'var(--opoint-light-grey)' }}
                  >
                    {comments && comments.length > 0 ? (
                      comments.map((post) => (
                        <div key={post.id} className='border-b pb-2'>
                          <p className='text-sm font-semibold'>
                            {post.user.username}
                          </p>
                          <CookedText cookedHtml={post.cooked} />
                        </div>
                      ))
                    ) : (
                      <p className='text-sm text-gray-500'>No comments yet.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {showSubTopicsSection && (
            <div className={`${showDescriptionSection || "w-full"}`}>
              <ProjectList topics={topic.sub_topics} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicDetail;
